import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { normaliseRegion } from '@/utils/currency';
import { toast } from 'sonner';
import { isQuoteOnlyDistributor } from '@/lib/quoteOnlyRegions';

const CartContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
};

export const CartProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [items, setItems] = useState([]);
  const saveTimer = useRef(null);
  const lastSaved = useRef(null);
  // working_basket_29oct2026 — quote basket + slide-out panel state
  const [quoteItems, setQuoteItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rvk_quote_items') || '[]'); } catch { return []; }
  });
  const [basketOpen, setBasketOpen] = useState(false);
  const [basketTab, setBasketTab] = useState('order');
  useEffect(() => {
    try { localStorage.setItem('rvk_quote_items', JSON.stringify(quoteItems)); } catch {}
  }, [quoteItems]);

  // ── Fetch cart from DB when user logs in ──
  useEffect(() => {
    if (!user || !token) {
      setItems([]);
      return;
    }
    const fetchCart = async () => {
      try {
        const { data } = await axios.get(`${API}/cart`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const dbItems = data.items || [];
        setItems(dbItems);
        lastSaved.current = JSON.stringify(dbItems);
      } catch {
        // If fetch fails (e.g. network), keep empty
      }
    };
    fetchCart();
  }, [user?.user_id, token]);

  // ── Debounced save to DB whenever items change ──
  useEffect(() => {
    if (!user || !token) return;
    const serialised = JSON.stringify(items);
    // Skip save if unchanged from last known DB state
    if (serialised === lastSaved.current) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/cart`, { items }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        lastSaved.current = serialised;
      } catch {
        // Silent fail – next mutation will retry
      }
    }, 600);

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [items, user?.user_id, token]);

  const addItem = useCallback((product, size, quantity = 1, priceType = 'salon_price') => {
    // ceo_instruction_ref: kenya_cart_gating_quote_only_24jul2026 —
    // quote-only distributors can never populate a cart, whatever
    // surface the add came from.
    if (isQuoteOnlyDistributor(user)) {
      toast.info('Ordering for your region is quote-based — please request a quote instead.');
      return;
    }
    // who_can_order_what_25oct2026 (CEO order): central guard — a team
    // member can never cart a professional-only product from ANY surface
    // (catalogue, detail, upsells, bundles). Server-side enforced too.
    if (user?.role === 'team_member') {
      const lvl = product?.product_access_level || product?.access_level
        || (product?.professional_only ? 'pro' : 'retail');
      if (lvl === 'pro') {
        toast.error('Professional only — ask your salon owner');
        return;
      }
    }
    const region = user?.selected_region || user?.region || 'uk';
    const topLevel = size?.[priceType] || 0;
    const regional = size?.regional_prices?.[normaliseRegion(region)]?.[priceType] || 0;
    const unitPrice = topLevel || regional;

    setItems(prev => {
      const key = `${product.product_id}_${size.size}`;
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i => i.key === key ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [...prev, {
        key,
        product_id: product.product_id,
        product_name: product.name,
        size: size.size,
        unit_price: unitPrice,
        quantity,
        image_url: product.image_url,
        category: product.category || '',
      }];
    });
  }, [user]);

  const removeItem = useCallback((key) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);

  const updateQuantity = useCallback((key, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.key !== key));
      return;
    }
    setItems(prev => prev.map(i => i.key === key ? { ...i, quantity } : i));
  }, []);

  const clearCart = useCallback(async () => {
    setItems([]);
    lastSaved.current = JSON.stringify([]);
    if (user && token) {
      try {
        await axios.delete(`${API}/cart`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* silent */ }
    }
  }, [user, token]);

  const addPackToCart = useCallback((packProducts, packMeta = null) => {
    setItems(prev => {
      let updated = [...prev];
      for (const p of packProducts) {
        const key = `${p.product_id || p.product_name}_${p.size}`;
        const existing = updated.find(i => i.key === key);
        const qty = p.quantity || 1;
        const unitPrice = p.unit_price || 0;
        if (existing) {
          updated = updated.map(i => i.key === key ? { ...i, quantity: i.quantity + qty } : i);
        } else {
          updated.push({
            key,
            product_id: p.product_id || '',
            product_name: p.product_name || '',
            size: p.size || '',
            unit_price: unitPrice,
            pack_id: p.pack_id || packMeta?.pack_id || null,
            pack_name: p.pack_name || packMeta?.pack_name || null,
            quantity: qty,
            image_url: p.image_url || '',
            category: p.category || '',
          });
        }
      }
      return updated;
    });
  }, []);

  const addQuoteItem = useCallback((product, size, quantity = 1, priceType = 'salon_price') => {
    const unitPrice = size?.[priceType] || size?.regional_prices?.[normaliseRegion(user?.region || 'uk')]?.[priceType] || 0;
    setQuoteItems(prev => {
      const key = `${product.product_id}_${size.size}`;
      const existing = prev.find(i => i.key === key);
      if (existing) return prev.map(i => i.key === key ? { ...i, quantity: i.quantity + quantity } : i);
      return [...prev, {
        key, product_id: product.product_id, product_name: product.name,
        size: size.size, unit_price: unitPrice, quantity,
        image_url: product.image_url, category: product.category || '',
      }];
    });
  }, [user]);

  const removeQuoteItem = useCallback((key) => setQuoteItems(prev => prev.filter(i => i.key !== key)), []);
  const updateQuoteQty = useCallback((key, quantity) => {
    if (quantity <= 0) { setQuoteItems(prev => prev.filter(i => i.key !== key)); return; }
    setQuoteItems(prev => prev.map(i => i.key === key ? { ...i, quantity } : i));
  }, []);
  const clearQuote = useCallback(() => setQuoteItems([]), []);
  const openBasket = useCallback((tab = 'order') => { setBasketTab(tab); setBasketOpen(true); }, []);
  const closeBasket = useCallback(() => setBasketOpen(false), []);

  const totalItems = items.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = items.reduce((s, i) => s + (i.unit_price * i.quantity), 0);

  return (
    <CartContext.Provider value={{
      items, addItem, addPackToCart, removeItem, updateQuantity, clearCart, totalItems, totalPrice,
      quoteItems, addQuoteItem, removeQuoteItem, updateQuoteQty, clearQuote,
      basketOpen, basketTab, setBasketTab, openBasket, closeBasket,
    }}>
      {children}
    </CartContext.Provider>
  );
};
